import { apiClient } from "@base/api/client";
import { isAxiosApiError } from "@base/lib/utils";
import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { draftDB } from "./db";
import type {
  DraftDetailResponse,
  DraftListItem,
  DraftListResponse,
  DraftSaveRequest,
  DraftSaveResponse,
} from "./schema";

async function fetchDraftList(projectId: number): Promise<DraftListResponse> {
  const response = await apiClient.get<DraftListResponse>(
    `/project-management/api/projects/${projectId}/daily-logs/drafts`,
  );
  return response.data;
}

export function draftListQueryOptions(projectId: number) {
  return {
    queryKey: ["drafts", projectId] as const,
    queryFn: () => fetchDraftList(projectId),
  };
}

export function useDraftListQuery(projectId: number) {
  return useSuspenseQuery(draftListQueryOptions(projectId));
}

async function fetchDraftDetail(projectId: number, draftId: string): Promise<DraftDetailResponse> {
  const response = await apiClient.get<DraftDetailResponse>(
    `/project-management/api/projects/${projectId}/daily-logs/drafts/${draftId}`,
  );
  return response.data;
}

export function draftDetailQueryOptions(projectId: number, draftId: string) {
  return {
    queryKey: ["drafts", projectId, draftId] as const,
    queryFn: () => fetchDraftDetail(projectId, draftId),
  };
}

export function useDraftDetailQuery(projectId: number, draftId: string) {
  return useSuspenseQuery(draftDetailQueryOptions(projectId, draftId));
}

export function getLatestDraft(results: DraftListItem[]): DraftListItem | undefined {
  if (results.length === 0) return undefined;

  return results.reduce((latest, draft) => (draft.updated_at > latest.updated_at ? draft : latest));
}

interface SaveDraftParams {
  projectId: number;
  body: DraftSaveRequest;
}

async function saveDraft({ projectId, body }: SaveDraftParams): Promise<DraftSaveResponse> {
  const response = await apiClient.post<DraftSaveResponse>(
    `/project-management/api/projects/${projectId}/daily-logs/drafts`,
    body,
  );
  return response.data;
}

export function useSaveDraftMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: saveDraft,
    onSuccess: (_, { projectId }) => {
      queryClient.invalidateQueries({ queryKey: ["drafts", projectId] });
    },
    onError: (error: unknown) => {
      // 서버 에러 (500 이상)
      if (isAxiosApiError(error)) {
        const { status } = error.response.data;
        if (status >= 500) {
          toast.info("Server is temporarily unavailable. Your draft is saved on this device.");
          return;
        }
      }

      // 연결 문제 (타임아웃 등)
      // if (isAxiosError(error) && !error.response) {
      //   toast.info("Connection issue. Your draft is saved on this device and will sync shortly.");
      //   return;
      // }

      // 기타 에러
      // toast.info("Could not sync to server. Your draft is saved on this device.");
    },
  });
}

export interface DeleteDailyLogDraftParams {
  projectId: number;
  draftId: string;
}

export async function deleteDailyLogDraft({ projectId, draftId }: DeleteDailyLogDraftParams): Promise<void> {
  await apiClient.delete(`/project-management/api/projects/${projectId}/daily-logs/drafts/${draftId}`);
}

export function useDeleteDraftMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: DeleteDailyLogDraftParams) => {
      const localDraft = await draftDB.dailyLogDrafts.get(params.draftId);

      await deleteDailyLogDraft(params).catch(() => {
        // 서버에 없는 로컬 전용 draft일 수 있으므로 서버 에러는 무시
      });

      if (localDraft) {
        await draftDB.dailyLogDrafts.delete(params.draftId);
      }
    },
    onSuccess: (_, { projectId }) => {
      toast.success("Draft deleted successfully");
      queryClient.invalidateQueries({ queryKey: ["drafts", projectId] });
    },
    onError: () => {
      toast.error("Failed to delete draft");
    },
  });
}
